import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";

import { useTheme, useLoyalty } from "../../../hooks";
import { logEvent } from "firebase/analytics";
import analytics from "../../../context/analytics";

//svgs
import { NoticeIconNew, Time as TimeIcon } from "../../../components/svgs";
import {
  Button,
  DynamicButton,
  Span,
  StyledText,
} from "../../../components/common/basic";
import { BackButton, FeatureHeader } from "../../../components/common";
import OptInWarningModal from "./component/OptInWarningModal";
import EligibilityModal from "./component/EligibilityModal";
import useSpeedShoppingPoints from "../speedShopping/hooks/useSpeedShoppingPoints";
import loadingGif from "../../../components/images/loader.gif";

const note = [
  `Earn points by mashing up. 0.05 GHS = 1 point.`,
  `Only top rankers have the opportunity to capture a reward.`,
  `Prizes are updated on stock availability.`,
  `Points from mashup purchases made via Momo reflects after 24hrs`,
  `You can only win once during the capture period.`,
  `Winners will be notified via call from 0244300000 and updated on the app. `,
];

const steps = [
  `Capture an image on the displayed fast moving GIF.`,
  `Share the captured image.`,
  `Choose your pickup location to receive your reward.`,
];

const pointsBuildingStartDate = new Date("2025-01-24T11:09");

const CapturenShareHome = () => {
  const { themeContext, theme } = useTheme();
  const [canStartCapture, setCanStartCapture] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [pointsBuildingCountdown, setPointsBuildingCountdown] = useState(null);
  const [pointsBuildingStarted, setPointsBuildingStarted] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [fetchingEntries, setFetchingEntries] = useState(true);
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);

  const token = JSON.parse(localStorage.getItem("token"));

  const {
    setCapture,
    fetchCaptureShare,
    captureAndShareData,
    selectedLoyaltyData,
    captureEntries,
    hasOptedIn,
    setHasOptedIn,
    submitOptInStatus,
  } = useLoyalty();

  const {
    userPoints,
    getLoyaltyPoints,
  } = useSpeedShoppingPoints();
  const { pointsRounded, isQualified } = userPoints || {};

  const { canCapture } = captureAndShareData?.promoData || {};

  const { promoData: { bannerImage } = {} } = selectedLoyaltyData || {};

  const history = useHistory();

  let themeColor = "background";
  let background = theme.colors[themeContext.activeTheme][themeColor];

  useEffect(() => {
    analytics().then((anl) =>
      logEvent(anl, "screen_view", {
        firebase_screen: "CaptureAndShareHome",
        firebase_screen_class: "CaptureAndShareHome",
      })
    );
    checkStartTime();
    fetchCaptureShare(captureAndShareData?.code);
    getLoyaltyPoints({ payload: {} });
  }, []);

  const initiatePointsBuildingCountdown = useCallback(() => {
    const targetDate = new Date(pointsBuildingStartDate);

    if (isNaN(targetDate)) {
      console.error("Invalid date format. Please provide a valid date.");
      return;
    }

    function calculateRemainingTime() {
      const now = new Date();
      const timeDiff = targetDate - now;

      if (timeDiff <= 0) {
        console.log("Countdown finished!");
        setPointsBuildingStarted(true);
        clearInterval(intervalId);
        return;
      }

      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      const formattedTime = `${days}d:${String(hours).padStart(
        2,
        "0"
      )}h:${String(minutes).padStart(2, "0")}m:${String(seconds).padStart(
        2,
        "0"
      )}s`;
      setPointsBuildingCountdown(formattedTime);
    }

    calculateRemainingTime();
    const intervalId = setInterval(calculateRemainingTime, 1000);
  }, [pointsBuildingStartDate]);

  const onOptInClick = async () => {
    setHasOptedIn(true);
    await submitOptInStatus(true);
  };

  useEffect(() => {
    if (new Date() > pointsBuildingStartDate) {
      setPointsBuildingStarted(true);
    } else {
      // Run timer
      initiatePointsBuildingCountdown();
    }
  }, []);

  const checkStartTime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0, 0);
    const startTimeMs = 28800000; // 8hr from start of day (8:00am)
    const now = new Date();
    const timeDiff = now.getTime() - today.getTime();
    setTimeRemaining(startTimeMs - timeDiff);
    const status = timeDiff >= startTimeMs;
    setCanStartCapture(status);
    setTimeout(checkStartTime, 1000);
  };

  const showOptInWarning = () => {
    setShowWarningModal(true);
  };

  const onClose = async (hasOptedIn) => {
    setShowWarningModal(false);
    if (hasOptedIn) {
      await submitOptInStatus(hasOptedIn);
      history.push("/capturenshare/points");
    }
  };

  useEffect(() => {
    if (captureEntries) {
      setFetchingEntries(false);
    }
  }, [captureEntries]);

  useEffect(() => {
    const noticeRendered =
      localStorage.getItem("isEligibleNotice") === "rendered";
    if (pointsRounded !== -1 && canCapture && !noticeRendered) {
      setShowEligibilityModal(true);
      localStorage.setItem("isEligibleNotice", "rendered");
    }
  }, [pointsRounded, canCapture]);

  const goToHome = () => {
    history.push(`/home?token=${token}`);
  };

  return (
    <>
      <MainHolder>
        <Children>
          <BackButton onClick={goToHome} />
          <FeatureHeader
            image={bannerImage}
            title={["Capture & Share", "Promo"]}
            buttonText="My Points"
            onClick={() =>
              hasOptedIn
                ? history.push("/capturenshare/points")
                : showOptInWarning()
            }
            content={
              !pointsBuildingStarted && (
                <InfoPill
                  content={`Points building starts in ${
                    pointsBuildingCountdown ?? "..."
                  }`}
                />
              )
            }
            background={background}
            secondaryBackground={"#fff"}
          />

          <Body>
            <div style={{ boxSizing: "border-box", padding: "0 15px" }}>
              {pointsRounded === -1 ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <img style={{ width: "48px" }} src={loadingGif} />
                  <StyledText>Checking Eligibility...</StyledText>
                </div>
              ) : (
                <></>
              )}
              <StyledText
                size={24}
                style={{
                  fontFamily: "MTNRegularItalic",
                  margin: "15px 0 0px 0",
                }}
              >
                <Span style={{ fontFamily: "MTNBoldItalic" }}>{`Yo`}</Span>
                {" Pulsers!"}
              </StyledText>

              <StyledText
                // letterSpacing={-0.38}
                height={17}
                style={{ fontFamily: "MTNRegular" }}
                size={13}
              >
                Love is in the air!! No time to waste!! Capture an image from
                the fast playing GIF to win amazing rewards from{" "}
                <Span style={{ fontFamily: "MTNBoldItalic" }}>
                  10th to 14th February, 2025.
                </Span>
              </StyledText>
              <StyledText style={{ fontFamily: "MTNRegular" }} size={13}>
                *Terms & Conditions apply*
              </StyledText>

              <Divider style={{ margin: "40px 0 20px 0" }} />

              <StyledText
                size={24}
                style={{ fontFamily: "MTNMedium", marginTop: "0px" }}
              >{`How it works`}</StyledText>
              {steps.map((note, index) => {
                return (
                  <NotesContainer key={index}>
                    <StyledText
                      size={13}
                      style={{ fontFamily: "MTNRegular", marginBlockStart: 0 }}
                    >
                      <Span style={{ fontFamily: "MTNBold" }}>
                        Step {index + 1}:{" "}
                      </Span>
                      {note}
                    </StyledText>
                  </NotesContainer>
                );
              })}

              <Divider style={{ margin: "30px 0 30px 0" }} />

              <StyledText
                size={24}
                style={{ fontFamily: "MTNMedium", marginTop: "0px" }}
              >{`Game Rules`}</StyledText>
              {note.map((note, index) => {
                return (
                  <NotesContainer key={index}>
                    <CircleContainer>
                      <Dot color={"#ffcc00"} />
                    </CircleContainer>
                    <StyledText
                      size={13}
                      style={{ fontFamily: "MTNRegular", marginBlockStart: 0 }}
                    >
                      {note}
                    </StyledText>
                  </NotesContainer>
                );
              })}

              {!hasOptedIn && (
                <DynamicButton
                  color={"#ffcc00"}
                  textColor={"#000"}
                  label="Opt in"
                  onClick={onOptInClick}
                />
              )}

              {captureEntries?.length < 1 && canCapture && isQualified && (
                <DynamicButton
                  color={"#ffcc00"}
                  disabled={fetchingEntries}
                  textColor={"#000"}
                  label="Capture"
                  onClick={() => {
                    history.push("/capturenshare/capture");
                  }}
                />
              )}

              {!!captureEntries?.length && (
                <DynamicButton
                  color={"#ffcc00"}
                  textColor={"#000"}
                  label="Track reward"
                  onClick={() => {
                    history.push("/capturenshare/history");
                  }}
                />
              )}
            </div>
          </Body>
        </Children>
      </MainHolder>
      <OptInWarningModal isOpen={showWarningModal} onClose={onClose} />
      {hasOptedIn && (
        <EligibilityModal
          isOpen={showEligibilityModal}
          onClose={() => setShowEligibilityModal(false)}
          isEligible={isQualified}
        />
      )}
    </>
  );
};

const InfoPill = ({ content }) => {
  return (
    <Button
      style={{
        width: "90%",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        cursor: "pointer",
      }}
      color={"white"}
      onClick={() => {}}
    >
      <StyledText
        style={{
          marginRight: 10,
          fontFamily: "MTNRegular",
          display: "flex",
          alignItems: "center",
          gap: "5px",
        }}
        size={14}
      >
        <NoticeIconNew /> {content}
      </StyledText>
    </Button>
  );
};

const MainHolder = styled.div`
  display: grid;
  align-content: space-between;
  grid-template-rows: auto auto 1fr;
  overflow: hidden;
  scrollbar-width: none;
  background-color: ${({ activeTheme }) => activeTheme};
  height: 700px;
  @media screen and (max-width: 480px) {
    border-radius: 0px;
    padding-bottom: 5px;
    height: 100vh;
  }
`;

const Children = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Body = styled.div`
  padding: 10px;
  margin: 5px;
`;

const NotesContainer = styled.div`
  display: flex;
`;

const CircleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  padding-inline: 10px;
  margin-top: 5px;
`;

const Dot = styled.div`
  width: 15px;
  height: 15px;
  border-radius: 40px;
  background-color: ${({ color }) => color};
  margin-right: 10px;
`;

const Divider = styled.div`
  height: 0.5px;
  background-color: #ddd;
`;

export default CapturenShareHome;
